import Input from '~/components/Inputs/Input';
import IntlTelInput from 'intl-tel-input/react';
import 'intl-tel-input/build/css/intlTelInput.css';
import Content from '@referralexchange/rea-ui-react/src/atoms/Content';
import PropTypes from 'prop-types';
import { useEffect, useState, useRef } from 'react';
import { sendTwoFA } from '~/utils/Fetch/Lightning';
import { getLeadUUID, clearLeadUUID } from '~/utils/UUID';
import { css, StyleSheet } from 'aphrodite';
import { motion } from 'framer-motion';
import { genericFunnels } from '~/config/funnel';

import { POSTCONVERT_URL } from '~/utils/FetchingData';
import { useDisplayNameOrFirstName } from '~/utils/AgentUtils';
import inputStyles from '../../../Funnel/experiments/reskin/molecules/Input.module.scss';

const TwoFA = (props) => {
  const {
    nextScreen,
    twoFAFirstName,
    twoFALastName,
    setTwoFAFirstName,
    setTwoFALastName,
    setTwoFAEmail,
    setTwoFAPhone,
    twoFAPhone,
    twoFAEmail,
    agent,
    twoFA,
    clientPhoneCountry,
    setClientPhoneCountry,
    funnel,
    setConsent,
    consent,
  } = props;

  const itiRef = useRef();

  const [loadSkip, setLoadSkip] = useState(false);
  const [phoneFocused, setPhoneFocused] = useState(false);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    setTimeout(() => {
      setLoadSkip(true);
    }, 3000);
  }, []);

  const screenKey = 'TwoFA';
  const displayNameOrFirstName = useDisplayNameOrFirstName(agent);
  const header = () => (
    <div className="MessageAgentForm__screen-heading">
      {genericFunnels.includes(funnel)
        ? "We're preparing to connect you to at least 3 agents. Please verify the following information to get connected sooner:"
        : `We're preparing to connect you to ${displayNameOrFirstName}. Please verify the following information to get connected sooner:`}
    </div>
  );

  const next = () => {
    // Use intl-tel-input validation
    const instance = itiRef.current.getInstance();
    if (!instance.isValidNumber()) {
      const errorCode = instance.getValidationError();
      const errorMap = [
        'Please enter a valid phone number',
        'Invalid country code - please check your selection',
        'Phone number is too short - please enter more digits',
        'Phone number is too long - please check your entry',
        'Please enter a valid phone number',
      ];
      const errorMessage =
        errorMap[errorCode] || 'Please enter a valid phone number!';
      setErrors([{ message: errorMessage, type: 'phoneValidation' }]);
      return;
    }

    // Clear any existing errors
    setErrors([]);

    sendTwoFA(getLeadUUID(), twoFAPhone);
    const consent_text_el = document.getElementById('two-fa-consent');
    setConsent({
      ...consent,
      consent_text_original:
        'By clicking "Text Confirmation Code", I am providing my esign and express written consent to allow ReferralExchange and our affiliated Participating Agents, or parties calling on their behalf, to contact me at the phone number above for marketing purposes, including through the use of calls, SMS/MMS, prerecorded and/or artificial voice messages using an automated dialing system to provide agent info, even if your number is listed on a corporate, state or federal Do-Not-Call list. Consent is not a condition for our service and you can revoke it at any time.',
      consent_text_shown: consent_text_el
        ? consent_text_el.textContent
        : consent.consent_text_shown,
    });
    nextScreen({ key: screenKey });
  };

  const consentText = (
    <>
      By clicking &quot;Text Confirmation Code&quot;, I am providing my esign
      and express written consent to allow ReferralExchange and our affiliated
      Participating Agents, or parties calling on their behalf, to contact me at
      the phone number above for marketing purposes, including through the use
      of calls, SMS/MMS, prerecorded and/or artificial voice messages using an
      automated dialing system to provide agent info, even if your number is
      listed on a corporate, state or federal Do-Not-Call list. Consent is not a
      condition for our service and you can revoke it at any time.
    </>
  );

  const initialCountry = clientPhoneCountry?.toLowerCase() || 'us';

  return (
    <div className="MessageAgentForm__screen" key="2fa">
      {header()}
      <div className="MessageAgentForm__contact-info">
        {twoFA.name && (
          <>
            <Input
              label="First name"
              name="client-first-name"
              autoComplete="given-name"
              required
              maxlength="36"
              pattern="[a-zA-Z0-9\- ]*"
              onChange={(value) => {
                setTwoFAFirstName(value);
              }}
              value={twoFAFirstName}
            />
            <Input
              label="Last name"
              name="client-last-name"
              autoComplete="family-name"
              required
              maxlength="36"
              pattern="[a-zA-Z0-9\- ]*"
              onChange={(value) => {
                setTwoFALastName(value);
              }}
              value={twoFALastName}
            />
          </>
        )}

        {(twoFA.email || twoFA.dupe_email || twoFA.agent_email) && (
          <Input
            label="Email"
            type="email"
            name="client-email"
            autoComplete="email"
            required
            maxlength="255"
            onChange={(value) => {
              setTwoFAEmail(value);
            }}
            value={twoFAEmail}
          />
        )}
        <div className="Tel">
          <div className={`Tel__wrapper ${css(styles.telWrapper)}`}>
            <div className="Tel__input-wrapper">
              <label
                htmlFor="client-phone"
                className={css(
                  styles.label,
                  (phoneFocused || twoFAPhone) && styles.labelFocused
                )}
              >
                Cell Number
              </label>
              <IntlTelInput
                initialValue={twoFAPhone}
                onChangeNumber={(phone) => {
                  setTwoFAPhone(phone);
                }}
                onChangeCountry={() => {
                  if (itiRef.current) {
                    const instance = itiRef.current.getInstance();
                    if (instance) {
                      const countryData = instance.getSelectedCountryData();
                      setClientPhoneCountry(countryData?.iso2?.toUpperCase());
                    }
                  }
                }}
                ref={itiRef}
                initOptions={{
                  initialCountry,
                  preferredCountries: ['us', 'ca'],
                  countrySearch: true,
                  formatOnDisplay: true,
                  nationalMode: true,
                  autoPlaceholder: 'polite',
                  useFullscreenPopup: false,
                  formatAsYouType: true,
                  strictMode: true,
                  validationNumberTypes: ['FIXED_LINE_OR_MOBILE'],
                  loadUtils: () => import('intl-tel-input/utils'),
                }}
                inputProps={{
                  id: 'client-phone',
                  name: 'client-phone',
                  autoComplete: 'tel',
                  onFocus: () => setPhoneFocused(true),
                  onBlur: () => setPhoneFocused(false),
                  className: css(styles.telInput),
                  required: true,
                  placeholder: ' ',
                  'data-testid': 'tel-input',
                }}
              />
            </div>
          </div>
        </div>
      </div>
      {errors && errors.length > 0 && (
        <ul className={inputStyles['Input__errors']}>
          {errors.map((error, index) => (
            <li key={index}>{error.message}</li>
          ))}
        </ul>
      )}
      <div className="MessageAgentForm__screen-controls">
        {genericFunnels.includes(funnel) && loadSkip ? (
          <motion.button
            className="--alt"
            type="button"
            initial="pageInitial"
            animate="pageAnimate"
            variants={{
              pageInitial: {
                opacity: 0,
              },
              pageAnimate: {
                opacity: [0, 1],
                transition: {
                  duration: 0.3,
                },
              },
            }}
            onClick={async () => {
              const leadUUID = getLeadUUID();
              clearLeadUUID();
              const referrer = localStorage.getItem('referrer') || '';
              const postConvertURL = await POSTCONVERT_URL({ uuid: leadUUID });
              if (postConvertURL) {
                window.location.href = `${postConvertURL}&experience=tv&referrer=${referrer}`;
              }
            }}
          >
            Skip
          </motion.button>
        ) : (
          <div />
        )}
        <div className="MessageAgentForm__screen-control-text-container">
          <button onClick={next} type="button">
            Text Confirmation Code
          </button>
        </div>
      </div>
      <div id="two-fa-consent" className={css(styles.consent)}>
        <Content>
          <small>{consentText}</small>
        </Content>
      </div>
    </div>
  );
};

TwoFA.propTypes = {
  nextScreen: PropTypes.func.isRequired,
  screensSeen: PropTypes.object.isRequired,
  agent: PropTypes.object.isRequired,
  twoFA: PropTypes.object.isRequired,
  setConsent: PropTypes.func.isRequired,
  consent: PropTypes.object.isRequired,
};

TwoFA.defaultProps = {
  nextScreen: () => {},
  screensSeen: new Set(),
  agent: {},
  setConsent: () => {},
  consent: {},
};

const styles = StyleSheet.create({
  consent: {
    marginTop: 16,
  },
  telWrapper: {
    overflow: 'visible',
    height: 'unset',
  },
  skip: {
    textDecoration: 'underline',
  },
  telLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: '#000',
    marginBottom: 8,
  },
  telInput: {
    width: '100%',
    borderRadius: 8,
  },
  label: {
    position: 'absolute',
    fontSize: 16,
    left: 50,
    lineHeight: '20px',
    top: '50%',
    transform: 'translateY(-50%)',
    transition:
      'font-size 100ms ease-in-out, line-height 100ms ease-in-out, top 100ms ease-in-out',
    pointerEvents: 'none',
    color: 'rgba(39,39,39,.6)',
    zIndex: 4,
  },
  labelFocused: {
    lineHeight: '12px',
    fontSize: 12,
    top: 12,
  },
});

export default TwoFA;
